import { collection, deleteDoc, doc, getDoc, getDocs, getCountFromServer, limit, orderBy, query, serverTimestamp, setDoc, startAfter, Timestamp, updateDoc, where, type QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import type { ObsessLessUser } from '../../../types/commonTypes';

const COLLECTION_NAME = 'referral_codes';

export interface ReferralCodeData {
  code: string;
  discountPercent: number;
  isActive: boolean;
  usesCount: number;
  createdAt: Date;
  expiresAt: Date | null;
  revenueSharePercent: number;
  applyResultMessage: string;
}

export interface CreateReferralCodeData {
  code: string;
  discountPercent: number;
  isActive: boolean;
  revenueSharePercent: number;
  applyResultMessage: string;
  expiresAt: Date | null;
}

export interface UpdateReferralCodeData {
  discountPercent: number;
  isActive: boolean;
  revenueSharePercent: number;
  applyResultMessage: string;
  expiresAt: Date | null;
}

export interface UsersByCodeResult {
  users: ObsessLessUser[];
  lastDoc: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

class ReferralCodesService {
  private static instance: ReferralCodesService;

  private constructor() {}

  public static getInstance(): ReferralCodesService {
    if (!ReferralCodesService.instance) {
      ReferralCodesService.instance = new ReferralCodesService();
    }
    return ReferralCodesService.instance;
  }

  /**
   * Convert Firestore timestamp to Date object
   */
  private convertTimestampToDate(timestamp: unknown): Date {
    if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
      return (timestamp as Timestamp).toDate();
    }
    if (typeof timestamp === 'string') {
      return new Date(timestamp);
    }
    if (typeof timestamp === 'number') {
      return new Date(timestamp * 1000);
    }
    console.warn('Invalid timestamp format:', timestamp);
    return new Date();
  }

  public async getAllReferralCodes(): Promise<ReferralCodeData[]> {
    const codesRef = collection(db, COLLECTION_NAME);
    const q = query(codesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        code: data.code,
        discountPercent: data.discountPercent,
        isActive: data.isActive,
        usesCount: data.usesCount,
        createdAt: this.convertTimestampToDate(data.createdAt),
        expiresAt: data.expiresAt ? this.convertTimestampToDate(data.expiresAt) : null,
        revenueSharePercent: data.revenueSharePercent,
        applyResultMessage: data.applyResultMessage,
      } as ReferralCodeData;
    });
  }

  public async createReferralCode(data: CreateReferralCodeData): Promise<ReferralCodeData> {
    const codeUppercase = data.code.trim().toUpperCase();
    const codeRef = doc(db, COLLECTION_NAME, codeUppercase);

    // Check if code already exists
    const existingDoc = await getDoc(codeRef);
    if (existingDoc.exists()) {
      throw new Error(`Referral code "${codeUppercase}" already exists`);
    }

    const now = new Date();
    
    // Convert expiresAt to Timestamp if provided
    const expiresAtTimestamp = data.expiresAt ? Timestamp.fromDate(data.expiresAt) : null;

    await setDoc(codeRef, {
      code: codeUppercase,
      discountPercent: data.discountPercent,
      isActive: data.isActive,
      revenueSharePercent: data.revenueSharePercent,
      applyResultMessage: data.applyResultMessage,
      usesCount: 0,
      createdAt: serverTimestamp(),
      expiresAt: expiresAtTimestamp,
    });

    // Return the created code with client-side date for immediate UI update
    return {
      code: codeUppercase,
      discountPercent: data.discountPercent,
      isActive: data.isActive,
      revenueSharePercent: data.revenueSharePercent,
      applyResultMessage: data.applyResultMessage,
      usesCount: 0,
      createdAt: now,
      expiresAt: data.expiresAt,
    };
  }

  public async updateReferralCode(code: string, data: UpdateReferralCodeData): Promise<ReferralCodeData> {
    const codeRef = doc(db, COLLECTION_NAME, code);

    // Check if code exists
    const existingDoc = await getDoc(codeRef);
    if (!existingDoc.exists()) {
      throw new Error(`Referral code "${code}" not found`);
    }

    const currentData = existingDoc.data();
    
    // Convert expiresAt to Timestamp if provided
    const expiresAtTimestamp = data.expiresAt ? Timestamp.fromDate(data.expiresAt) : null;

    await updateDoc(codeRef, {
      discountPercent: data.discountPercent,
      isActive: data.isActive,
      revenueSharePercent: data.revenueSharePercent,
      applyResultMessage: data.applyResultMessage,
      expiresAt: expiresAtTimestamp,
      updatedAt: serverTimestamp(),
    });

    // Return the updated code with client-side date for immediate UI update
    return {
      code: code,
      discountPercent: data.discountPercent,
      isActive: data.isActive,
      revenueSharePercent: data.revenueSharePercent,
      applyResultMessage: data.applyResultMessage,
      usesCount: currentData.usesCount || 0,
      createdAt: this.convertTimestampToDate(currentData.createdAt),
      expiresAt: data.expiresAt,
    };
  }

  public async deleteReferralCode(code: string): Promise<void> {
    const codeRef = doc(db, COLLECTION_NAME, code);
    await deleteDoc(codeRef);
  }

  public async getUsersCountByReferralCode(code: string, finishedOnboarding?: boolean): Promise<number> {
    const usersRef = collection(db, 'user_info');
    const constraints = [
      where('referralCode', '==', code),
      ...(finishedOnboarding ? [where('hasFinishedOnboarding', '==', true)] : []),
    ];
    const q = query(usersRef, ...constraints);
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  }

  public async getUsersByReferralCode(
    code: string,
    pageSize: number = 50,
    lastDoc?: QueryDocumentSnapshot,
    finishedOnboarding?: boolean
  ): Promise<UsersByCodeResult> {
    const usersRef = collection(db, 'user_info');

    const baseConstraints = [
      where('referralCode', '==', code),
      ...(finishedOnboarding ? [where('hasFinishedOnboarding', '==', true)] : []),
      orderBy('createdAt', 'desc'),
    ];

    let q = query(usersRef, ...baseConstraints, limit(pageSize + 1));

    // Add pagination cursor if provided
    if (lastDoc) {
      q = query(usersRef, ...baseConstraints, startAfter(lastDoc), limit(pageSize + 1));
    }

    const snapshot = await getDocs(q);
    const hasMore = snapshot.docs.length > pageSize;
    const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;

    const users = docs.map((doc) => {
      const data = doc.data();
      return {
        uid: doc.id,
        email: data.email ?? null,
        displayName: data.displayName ?? null,
        createdAt: this.convertTimestampToDate(data.createdAt),
        hasFinishedOnboarding: data.hasFinishedOnboarding ?? false,
        onboardingProgress: data.onboardingProgress ?? '',
        referralCode: data.referralCode,
        questJourney: data.questJourney
          ? {
              createdAt: this.convertTimestampToDate(data.questJourney.createdAt),
              currentDay: data.questJourney.currentDay,
              isCurrentDayCompleted: data.questJourney.isCurrentDayCompleted,
              journeyDayStartedAt: this.convertTimestampToDate(data.questJourney.journeyDayStartedAt),
              updatedAt: this.convertTimestampToDate(data.questJourney.updatedAt),
              journeyDayCompletedAt: data.questJourney.journeyDayCompletedAt
                ? this.convertTimestampToDate(data.questJourney.journeyDayCompletedAt)
                : undefined,
            }
          : undefined,
      } as ObsessLessUser;
    });

    return {
      users,
      lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
      hasMore,
    };
  }
}

export const referralCodesService = ReferralCodesService.getInstance();
